import { useQuery } from "@tanstack/react-query";
import CardReporting, { type CardReportingItem } from "components/cards/CardReporting/CardReporting";
import { RequestStatusRenderer } from "components/requestStatsRenderer/RequestStatusRenderer";
import {
  convertInteractionDisplayName,
  fallbackToZero,
  type PerformanceFilter,
  type QueryFilter,
} from "features/Library/Common/utils/performanceUtils";
import { GenericPerformanceListWithServerPagination } from "features/Reporting/Content/shared/GenericPerformanceListWithServerPagination";
import { peopleReportingService } from "features/Reporting/services/peopleReportingService";
import { type PeopleInteractionItem } from "features/Reporting/types/people";
import { type ColumnOption } from "interfaces/columnOptions";
import { useCallback, useMemo } from "react";
import dateTimeUtils from "utils/dateTimeUtils";
import { fetchPeopleInteractionTablePaginated } from "../queries";
import { type UserAssetTypes } from "features/People/Users/Performance/types";
import { type PaginationParams } from "features/Library/Common/models";

const fetchPeopleInteractionStatistics = async ({ queryKey }: QueryFilter): Promise<CardReportingItem[]> => {
  const result = await peopleReportingService.activity.getPeopleInteractionStatistics(queryKey[1]);

  return [
    {
      stat: fallbackToZero(result.data.Users),
      statDescription: "Users",
      popupBody:
        "Total number of users who interacted with the platform, opened an email, started a flow, etc. during the specified time period.",
    },
    {
      stat: fallbackToZero(result.data.Interactions),
      statDescription: "Interactions",
      popupBody:
        "Total number of user interactions for all participating users across all interaction types during the specified time period.",
    },
    {
      stat: fallbackToZero(result.data.AvgInteractions),
      statDescription: "Avg. Interactions/User",
      popupBody:
        "Total number of user interactions across all types divided by the total number of participating users during the specified time period.",
    },
  ];
};

export interface Props {
  dateFilter: PerformanceFilter;
  setSelectedDrilldown: (category: UserAssetTypes) => void;
}

type InteractionColumnOption = ColumnOption<PeopleInteractionItem> & { sortKey?: string };

export const UserInteraction = ({ dateFilter, setSelectedDrilldown }: Props) => {
  const peopleInteractionStatisticsQuery = useQuery({
    queryFn: fetchPeopleInteractionStatistics,
    queryKey: ["people interaction statistics", dateFilter],
  });

  const queryKey = useMemo(() => ["people interaction table", dateFilter] as const, [dateFilter]);

  const fetchTableData = useCallback(
    (params: PaginationParams, signal?: AbortSignal) =>
      fetchPeopleInteractionTablePaginated(dateFilter, params, signal),
    [dateFilter],
  );

  const columns: InteractionColumnOption[] = useMemo(
    () => [
      {
        name: "Type",
        width: 7,
        isSortable: true,
        sortKey: "InteractionType",
        render(item) {
          return (
            <button className="invisibleButton" onClick={() => setSelectedDrilldown(item.InteractionType)}>
              {convertInteractionDisplayName(item.InteractionType)}
            </button>
          );
        },
      },
      {
        name: "Users",
        width: 7,
        isSortable: true,
        sortKey: "UserCount",
        render(item) {
          return <span>{item.UserCount.toLocaleString()}</span>;
        },
      },
      {
        name: "Interactions",
        width: 7,
        isSortable: true,
        sortKey: "InteractionCount",
        render(item) {
          return <span>{item.InteractionCount.toLocaleString()}</span>;
        },
      },
      {
        name: "Last Activity",
        width: 7,
        isSortable: true,
        sortKey: "LastInteraction",
        render(item) {
          return <span>{item.LastInteraction ? dateTimeUtils.formatDate(item.LastInteraction) : "-"}</span>;
        },
      },
    ],
    [setSelectedDrilldown],
  );

  return (
    <div className="tabSpacer">
      <div>
        <RequestStatusRenderer state={peopleInteractionStatisticsQuery.status}>
          <CardReporting items={peopleInteractionStatisticsQuery.data!} />
        </RequestStatusRenderer>
      </div>
      <div className="tabSpacer">
        <GenericPerformanceListWithServerPagination
          columns={columns}
          filter={dateFilter}
          fetchPage={fetchTableData}
          queryKeyBase={queryKey}
          hideControls={false}
        />
      </div>
    </div>
  );
};
